import { type ComponentPropsWithoutRef } from 'react';
import { useIntl } from 'react-intl';
import { Select } from '@repo/design-system-kit/components/select/select';

export const YEARLY_TRANSACTION_VOLUME_OPTIONS = [
  '0-10000',
  '10000-50000',
  '50000-100000',
  '100000-500000',
  '500000-',
] as const;

export type YearlyTransactionVolumeRange = (typeof YEARLY_TRANSACTION_VOLUME_OPTIONS)[number];

export interface TransactionVolumeSelectProps extends Omit<
  ComponentPropsWithoutRef<'div'>,
  'onChange'
> {
  errorMessage?: string;
  onChange?: (value: YearlyTransactionVolumeRange) => void;
  value?: YearlyTransactionVolumeRange | null;
}

export function TransactionVolumeSelect({
  className,
  errorMessage,
  onChange,
  value,
  ...props
}: TransactionVolumeSelectProps) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: 'company-creation-kit.transaction-volume-select.label',
  });

  const description = intl.formatMessage({
    id: 'company-creation-kit.transaction-volume-select.description',
  });

  const placeholder = intl.formatMessage({
    id: 'company-creation-kit.transaction-volume-select.placeholder',
  });

  const options = [
    {
      code: YEARLY_TRANSACTION_VOLUME_OPTIONS[0],
      name: intl.formatMessage({
        id: 'company-creation-kit.transaction-volume-select.option-0-10000',
      }),
    },
    {
      code: YEARLY_TRANSACTION_VOLUME_OPTIONS[1],
      name: intl.formatMessage({
        id: 'company-creation-kit.transaction-volume-select.option-10000-50000',
      }),
    },
    {
      code: YEARLY_TRANSACTION_VOLUME_OPTIONS[2],
      name: intl.formatMessage({
        id: 'company-creation-kit.transaction-volume-select.option-50000-100000',
      }),
    },
    {
      code: YEARLY_TRANSACTION_VOLUME_OPTIONS[3],
      name: intl.formatMessage({
        id: 'company-creation-kit.transaction-volume-select.option-100000-500000',
      }),
    },
    {
      code: YEARLY_TRANSACTION_VOLUME_OPTIONS[4],
      name: intl.formatMessage({
        id: 'company-creation-kit.transaction-volume-select.option-500000-',
      }),
    },
  ];

  return (
    <div className={className} data-test-transaction-volume-input {...props}>
      <Select
        data-test-transaction-volume-select
        description={errorMessage ? undefined : description}
        errorMessage={errorMessage}
        isInvalid={Boolean(errorMessage)}
        label={label}
        onSelectionChange={key => {
          const option = YEARLY_TRANSACTION_VOLUME_OPTIONS.find(opt => opt === key);
          if (option !== undefined) {
            onChange?.(option);
          }
        }}
        placeholder={placeholder}
        selectedKey={value}
      >
        {options.map(option => (
          <Select.Option
            data-test-transaction-volume-option={option.code}
            id={option.code}
            key={option.code}
          >
            {option.name}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
}
